import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, CreditCard, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES, CALENDLY_DISCOVERY_URL } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';
import { PaymentModal } from '../components/PaymentModal';

const CoachingOnlinePage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [paymentModal, setPaymentModal] = useState({ isOpen: false, item: null });

  const openPaymentModal = (type) => {
    // Récupérer le forfait depuis pricingData
    const cat = pricingCategories.find((c) => c.id === type);
    const pkg = cat && cat.durations && cat.durations[0] && cat.durations[0].packages && cat.durations[0].packages[0];
    if (!cat || !pkg) return;
    const totalPrice = parseFloat(pkg.total || pkg.price);
    const monthly = parseFloat(pkg.price || totalPrice);
    setPaymentModal({
      isOpen: true,
      item: {
        name: `${cat.name} - ${pkg.sessions || pkg.label}`,
        price: monthly,
        total: totalPrice,
        type: cat.id,
        maxInstallments: totalPrice > 2500 ? 3 : totalPrice > 1500 ? 2 : 1,
        allowAnnual: true,
        months: 6,
      },
    });
  };

  // Animated counter hook
  const AnimatedCounter = ({ end, suffix = "", duration = 2000 }) => {
    const [count, setCount] = React.useState(0);
    const [ref, isVisible] = useIntersectionObserver();
    const started = React.useRef(false);
    React.useEffect(() => {
      if (isVisible && !started.current) {
        started.current = true;
        let start = 0;
        const step = end / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
        return () => clearInterval(timer);
      }
    }, [isVisible, end, duration]);
    return <span ref={ref}>{count}{suffix}</span>;
  };

  const features = [
    { icon: Dumbbell, title: fr ? "Programmes 100% sur mesure" : "100% custom programs", desc: fr ? "Musculation, HIIT, supersets — des programmes qui evoluent pour une progression constante." : "Strength, HIIT, supersets — programs that evolve for constant progression.", accent: "from-brand-400 to-brand-300" },
    { icon: Sparkles, title: fr ? "Plan alimentaire IA 7 jours" : "AI meal plan 7 days", desc: fr ? "Un plan nutritionnel personnalise genere par IA avec macros, images de plats et recettes etape par etape." : "A personalized nutrition plan generated by AI with macros, dish images and step-by-step recipes.", accent: "from-pink-400 to-rose-400", isNew: true },
    { icon: Calendar, title: fr ? "Agenda d'entrainement" : "Training schedule", desc: fr ? "Vos seances planifiees automatiquement dans votre agenda personnel avec rappels intelligents." : "Your sessions automatically scheduled in your personal calendar with smart reminders.", accent: "from-violet-400 to-purple-400", isNew: true },
    { icon: MessageCircle, title: fr ? "Suivi WhatsApp 24/7" : "WhatsApp support 24/7", desc: fr ? "Posez vos questions a tout moment. Votre coach vous repond personnellement." : "Ask questions at any time. Your coach responds personally.", accent: "from-brand-300 to-amber-400" },
    { icon: Video, title: fr ? "6 Zoom inclus (30 min, ≈1/mois)" : "6 included Zoom calls (30 min, ≈1/month)", desc: fr ? "6 rendez-vous sur votre accompagnement pour ajuster vos programmes et faire le point sur vos objectifs." : "6 meetings over your program to adjust your plans and review your goals.", accent: "from-amber-400 to-yellow-400" },
    { icon: Apple, title: fr ? "Suivi nutritionnel complet" : "Complete nutritional tracking", desc: fr ? "Base de 3M+ d'aliments, scan de codes-barres, analyse photo IA, tracker d'eau, favoris." : "3M+ food database, barcode scan, AI photo analysis, water tracker, favorites.", accent: "from-orange-400 to-amber-400" },
    { icon: Zap, title: fr ? "Supersets & minuterie" : "Supersets & timer", desc: fr ? "Creez des supersets avec minuterie automatique pour des seances intenses et structurees." : "Create supersets with automatic timer for intense, structured sessions.", accent: "from-yellow-400 to-orange-400", isNew: true },
    { icon: Flame, title: fr ? "Bibliotheque d'exercices" : "Exercise library", desc: fr ? "Creez et sauvegardez vos propres exercices dans votre bibliotheque personnelle." : "Create and save your own exercises in your personal library.", accent: "from-red-400 to-pink-400", isNew: true },
    { icon: BarChart3, title: fr ? "Statistiques detaillees" : "Detailed statistics", desc: fr ? "Progression, performances, badges, defis hebdomadaires, recap de semaine." : "Progression, performances, badges, weekly challenges, weekly recap.", accent: "from-blue-400 to-indigo-400" },
    { icon: Trophy, title: fr ? "Gamification motivante" : "Motivating gamification", desc: fr ? "Badges, trophees, niveaux, systeme de recompenses pour rester motive." : "Badges, trophies, levels, reward system to stay motivated.", accent: "from-amber-400 to-yellow-400" },
    { icon: Globe, title: fr ? "Messagerie coach dans l'app" : "In-app coach messaging", desc: fr ? "Echangez directement avec votre coach depuis l'application." : "Chat directly with your coach from the app.", accent: "from-amber-400 to-brand-400" },
    { icon: Monitor, title: fr ? "App accessible partout" : "App accessible everywhere", desc: fr ? "Votre programme dans votre poche. Compatible iPhone, Android, tablette." : "Your program in your pocket. Compatible iPhone, Android, tablet.", accent: "from-slate-400 to-zinc-400" },
  ];

  return (
    <main className="pt-32" data-testid="coaching-online-page">
      <SEOHead
        title="Coaching en Ligne | Personal Training a Distance | Body-Up Nyon"
        description="Coaching sportif en ligne par Body-Up Nyon. Programmes personnalises, suivi WhatsApp 24/7, plan alimentaire IA, seances Zoom. Votre coach ou que vous soyez."
        path="/coaching-online"
      />
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-black" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(255,107,53,0.10) 0%, transparent 70%)' }} />
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-brand-500/8 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-20 right-1/4 w-60 h-60 bg-brand-400/8 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-left"><BackNav /></div>
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-brand-500/10 border border-brand-500/20 rounded-full text-brand-400 text-xs font-bold mb-8 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5" />
              {fr ? "PACK TRANSFORMATION" : "TRANSFORMATION PACK"}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
              {fr ? "Coaching en Ligne" : "Online Coaching"}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 via-brand-300 to-brand-400" style={{ backgroundSize: '200% 100%', animation: 'gradient-shift 4s ease infinite' }}>
                {fr ? "100% Personnalise" : "100% Personalized"}
              </span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-6 leading-relaxed">
              {fr 
                ? "Transformez votre corps avec un suivi professionnel a distance. Programmes sur mesure, nutrition guidee par IA, et motivation au quotidien."
                : "Transform your body with professional remote coaching. Custom programs, AI-guided nutrition, and daily motivation."
              }
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-white/10 rounded-full mb-10">
              <Zap className="w-3.5 h-3.5 text-brand-400" />
              <span className="text-xs text-zinc-300">{fr ? "Via l'app" : "Via the app"}</span>
              <span className="text-xs font-black text-white tracking-tight">Body-Up</span>
              <ArrowRight className="w-3 h-3 text-zinc-500" />
            </div>
          </AnimatedSection>

          {/* Stats row */}
          <AnimatedSection delay={250}>
            <div className="flex justify-center gap-8 sm:gap-16 mb-12">
              {[
                { val: 3, suffix: "M+", label: fr ? "Aliments" : "Foods" },
                { val: 7, suffix: "j", label: fr ? "Plan IA" : "AI Plan" },
                { val: 24, suffix: "/7", label: fr ? "Support" : "Support" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-black text-white"><AnimatedCounter end={s.val} suffix={s.suffix} /></div>
                  <div className="text-[10px] sm:text-xs text-zinc-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <div className="relative inline-block">
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-500 to-brand-400 rounded-2xl blur-xl opacity-20" />
              <div className="relative bg-zinc-950/90 backdrop-blur-xl border border-brand-500/20 rounded-2xl p-8 max-w-sm mx-auto shadow-2xl shadow-brand-500/5">
                <p className="text-zinc-500 text-xs mb-2 uppercase tracking-widest font-medium">{fr ? "Pack Transformation · 6 mois" : "Transformation Pack · 6 months"}</p>
                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className="text-6xl font-black text-white tracking-tight">299</span>
                  <span className="text-xl text-zinc-500 font-medium">CHF</span>
                </div>
                <p className="text-zinc-600 text-xs mb-1">/{fr ? "mois" : "month"}</p>
                <p className="text-brand-400 text-xs font-semibold mb-8">{fr ? "Engagement 6 mois · Méthode personnalisée" : "6-month commitment · Personalized method"}</p>
                <a href="#booking" onClick={(e) => { e.preventDefault(); document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <Button className="w-full bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white font-bold text-base py-6 h-auto rounded-xl shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 transition-all duration-300 hover:scale-[1.03] group" data-testid="coaching-online-cta">
                    {fr ? "Commencer ma transformation" : "Start my transformation"}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1.5 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ What's included (Feature grid) ═══ */}
      <section className="py-16 lg:py-24 bg-black relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {fr ? "Tout ce qui est inclus" : "Everything included"}
              </h2>
              <p className="text-zinc-500 max-w-xl mx-auto text-sm">
                {fr ? "Une app complete + un coach dedie pour des resultats concrets" : "A complete app + a dedicated coach for concrete results"}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <AnimatedSection key={i} delay={i * 40}>
                <div className="group relative bg-zinc-900/40 border border-zinc-800/80 rounded-xl p-5 hover:border-zinc-700 transition-all duration-500 h-full overflow-hidden">
                  {/* Hover glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${f.accent} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500 rounded-xl`} />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.accent} bg-opacity-15 flex items-center justify-center`} style={{ background: `linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))` }}>
                        <f.icon className="w-4.5 h-4.5 text-brand-400" />
                      </div>
                      {f.isNew && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-brand-500/15 text-brand-400 border border-brand-500/25 uppercase tracking-wider">New</span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-white mb-1.5">{f.title}</h3>
                    <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ How it works (Timeline) ═══ */}
      <section className="py-16 lg:py-24 bg-black relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {fr ? "Comment ca marche ?" : "How does it work?"}
              </h2>
              <p className="text-zinc-500 text-sm">{fr ? "4 etapes simples pour commencer votre transformation" : "4 simple steps to start your transformation"}</p>
            </div>
          </AnimatedSection>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-brand-500/40 via-brand-400/20 to-transparent hidden sm:block" />
            
            <div className="space-y-8">
              {[
                { step: 1, title: fr ? "Inscription & Premier RDV Zoom" : "Registration & First Zoom", desc: fr ? "Inscrivez-vous sur l'app et reservez votre premier rendez-vous Zoom de 30 minutes pour l'onboarding avec Laurent." : "Sign up on the app and book your first 30-minute Zoom onboarding call with Laurent.", gradient: "from-brand-500 to-brand-400" },
                { step: 2, title: fr ? "Programme sur mesure" : "Custom program", desc: fr ? "Apres notre echange, je cree votre programme personnalise (entrainement + nutrition IA) directement dans l'app." : "After our call, I create your personalized program (training + AI nutrition) directly in the app.", gradient: "from-brand-400 to-brand-300" },
                { step: 3, title: fr ? "Entrainez-vous & trackez" : "Train & track", desc: fr ? "Suivez votre programme avec supersets et minuterie, loguez vos repas, consultez votre plan IA et trackez votre progression." : "Follow your program with supersets and timer, log meals, check your AI plan and track progression.", gradient: "from-amber-500 to-amber-400" },
                { step: 4, title: fr ? "Suivi continu & ajustements" : "Continuous follow-up", desc: fr ? "WhatsApp 24/7 pour vos questions + 1 Zoom/mois pour ajuster et optimiser vos resultats." : "WhatsApp 24/7 for questions + 1 Zoom/month to adjust and optimize results.", gradient: "from-yellow-500 to-yellow-400" },
              ].map((s, i) => (
                <AnimatedSection key={i} delay={i * 120}>
                  <div className="flex gap-5 items-start group">
                    <div className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white font-black text-sm">{s.step}</span>
                    </div>
                    <div className="pt-1">
                      <h3 className="text-base font-bold text-white mb-1.5">{s.title}</h3>
                      <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ App Features Showcase (Bento grid) ═══ */}
      <section className="py-16 lg:py-24 bg-zinc-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/20 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-500/10 border border-brand-500/20 rounded-full text-brand-400 text-xs font-bold mb-5 backdrop-blur-sm">
                <Zap className="w-3 h-3" />
                {fr ? "APP PREMIUM INCLUSE" : "PREMIUM APP INCLUDED"}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {fr ? "Une app pro dans votre poche" : "A pro app in your pocket"}
              </h2>
              <p className="text-zinc-500 max-w-xl mx-auto text-sm">
                {fr ? "Toutes les fonctionnalites pour atteindre vos objectifs" : "All features to reach your goals"}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {[
              { icon: Dumbbell, label: fr ? "Programmes personnalises" : "Custom programs" },
              { icon: Sparkles, label: fr ? "Plan alimentaire IA 7 jours" : "AI meal plan 7 days", highlight: true },
              { icon: Apple, label: fr ? "Suivi nutrition 3M+ aliments" : "Nutrition 3M+ foods" },
              { icon: Scan, label: fr ? "Scanner code-barres" : "Barcode scanner" },
              { icon: Calendar, label: fr ? "Agenda d'entrainement" : "Training calendar", highlight: true },
              { icon: Zap, label: fr ? "Supersets & minuterie" : "Supersets & timer", highlight: true },
              { icon: Flame, label: fr ? "Bibliotheque d'exercices" : "Exercise library", highlight: true },
              { icon: Activity, label: fr ? "Tracking performances" : "Performance tracking" },
              { icon: TrendingUp, label: fr ? "Graphiques de progression" : "Progression charts" },
              { icon: Trophy, label: fr ? "Badges & recompenses" : "Badges & rewards" },
              { icon: MessageCircle, label: fr ? "Messagerie coach" : "Coach messaging" },
              { icon: Target, label: fr ? "Photos avant/apres" : "Before/after photos" },
            ].map((f, i) => (
              <AnimatedSection key={i} delay={i * 25}>
                <div className={`group flex items-center gap-2.5 px-3 py-3 rounded-lg border transition-all duration-300 ${f.highlight ? 'bg-brand-500/5 border-brand-500/20 hover:border-brand-500/40 hover:bg-brand-500/10' : 'bg-zinc-900/40 border-zinc-800/60 hover:border-zinc-700'}`}>
                  <f.icon className={`w-4 h-4 shrink-0 ${f.highlight ? 'text-brand-400' : 'text-zinc-500 group-hover:text-brand-400'} transition-colors`} />
                  <span className="text-[11px] sm:text-xs text-zinc-300 leading-tight">{f.label}</span>
                  {f.highlight && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-400 shrink-0 animate-pulse" />}
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={400}>
            <div className="mt-10 text-center">
              <p className="text-zinc-600 text-xs mb-3">{fr ? "Découvrez l'app Body-Up pour votre coaching" : "Discover the Body-Up app for your coaching"}</p>
              <Link to="/espace-client">
                <Button variant="outline" className="border-brand-500/30 text-brand-400 hover:bg-brand-500/10 text-sm px-8 h-10 rounded-xl">
                  {fr ? "Découvrir l'app" : "Discover the app"}
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ Pricing CTA ═══ */}
      <section className="py-16 lg:py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 80%, rgba(255,107,53,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {fr ? "Pret a transformer votre corps ?" : "Ready to transform your body?"}
            </h2>
            <p className="text-zinc-500 mb-10 max-w-lg mx-auto text-sm">
              {fr ? "Rejoignez les clients Body-Up qui obtiennent des resultats concrets grace au coaching en ligne via l'app Body-Up." : "Join Body-Up clients who achieve concrete results with personalized online coaching via the Body-Up app."}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-px bg-gradient-to-b from-brand-500/30 via-brand-500/10 to-transparent rounded-2xl" />
              <div className="relative bg-zinc-950 rounded-2xl p-8 border border-zinc-800/50">
                <div className="text-center mb-8">
                  <p className="text-brand-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-3">{fr ? "Pack Transformation · 6 mois" : "Transformation Pack · 6 months"}</p>
                  <div className="flex items-baseline justify-center gap-1.5">
                    <span className="text-6xl font-black text-white tracking-tight">299</span>
                    <div className="text-left">
                      <span className="block text-sm text-zinc-500 font-medium">CHF</span>
                      <span className="block text-[10px] text-zinc-600">/{fr ? "mois" : "month"}</span>
                    </div>
                  </div>
                </div>
                
                <ul className="space-y-3 text-left mb-8">
                  {[
                    fr ? "Programmes 100% personnalises (muscu, HIIT, supersets)" : "100% custom programs (strength, HIIT, supersets)",
                    fr ? "Plan alimentaire IA sur 7 jours avec recettes" : "7-day AI meal plan with recipes",
                    fr ? "Agenda d'entrainement intelligent avec rappels" : "Smart training schedule with reminders",
                    fr ? "Suivi WhatsApp 24/7 avec Laurent" : "24/7 WhatsApp support with Laurent",
                    fr ? "6 Zoom de 30 minutes inclus (≈1/mois)" : "6 included 30-minute Zoom calls (≈1/month)",
                    fr ? "Messagerie coach integree dans l'app" : "In-app coach messaging",
                    fr ? "Suivi nutritionnel avance (3M+ aliments, scan IA)" : "Advanced nutritional tracking (3M+ foods, AI scan)",
                    fr ? "Bibliothèque d'exercices personnalisée" : "Personal exercise library",
                    fr ? "Statistiques, badges et defis motivants" : "Statistics, badges and motivating challenges",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <div className="w-4 h-4 rounded-full bg-brand-500/15 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5 text-brand-400" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>

                <a href="#booking" onClick={(e) => { e.preventDefault(); document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  <Button className="w-full bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white font-bold text-base py-6 h-auto rounded-xl shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 transition-all duration-300 hover:scale-[1.03] group" data-testid="coaching-online-bottom-cta">
                    {fr ? "Commencer maintenant" : "Start now"}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1.5 transition-transform" />
                  </Button>
                </a>
                
                <p className="text-zinc-700 text-[10px] mt-4 text-center">{fr ? "Premier rendez-vous Zoom offert pour l'onboarding" : "First Zoom meeting free for onboarding"}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══ Calendly Section ═══ */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-zinc-950 relative" id="booking">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-400/20 to-transparent" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-500/10 border border-brand-500/20 rounded-full text-brand-400 text-xs font-bold mb-5 backdrop-blur-sm">
                <Video className="w-3 h-3" />
                {fr ? "PREMIER RDV GRATUIT" : "FREE FIRST MEETING"}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {fr ? "Reservez votre Zoom d'onboarding" : "Book your onboarding Zoom"}
              </h2>
              <p className="text-zinc-500 max-w-lg mx-auto text-sm">
                {fr 
                  ? "30 minutes en visio avec Laurent pour discuter de vos objectifs et demarrer votre transformation."
                  : "30 minutes video call with Laurent to discuss your goals and start your transformation."
                }
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="relative">
              <div className="absolute -inset-px bg-gradient-to-b from-brand-500/20 to-transparent rounded-2xl" />
              <div className="relative bg-zinc-950 border border-zinc-800/50 rounded-2xl p-8 text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500/15 to-brand-400/15 border border-brand-500/20 flex items-center justify-center mx-auto mb-5">
                    <Video className="w-7 h-7 text-brand-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1.5">{fr ? "Démarrer votre coaching en ligne" : "Start your online coaching"}</h3>
                  <p className="text-sm text-zinc-400">{fr ? "Choisissez l'option qui vous convient" : "Pick the option that suits you"}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  {/* CTA 1 : Postuler pour un appel (qualification) */}
                  <button
                    onClick={() => navigate('/postuler-coaching?type=online')}
                    className="group relative overflow-hidden bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white font-bold px-5 py-4 rounded-xl shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40 transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-1"
                    data-testid="coaching-online-discovery-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      <span className="text-base">{fr ? "Postuler pour un appel" : "Apply for a call"}</span>
                    </div>
                    <span className="text-[11px] text-white/85 font-normal">{fr ? "30 min · Zoom · gratuit après qualification" : "30 min · Zoom · free after qualification"}</span>
                  </button>

                  {/* CTA 2 : Payer maintenant (Stripe) */}
                  <button
                    onClick={() => openPaymentModal('coaching-online')}
                    className="group bg-white/5 hover:bg-white/10 border-2 border-white/10 hover:border-brand-400/40 text-white font-semibold px-5 py-4 rounded-xl transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-1"
                    data-testid="coaching-online-buy-btn"
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-brand-400" />
                      <span className="text-base">{fr ? "Payer maintenant" : "Pay now"}</span>
                    </div>
                    <span className="text-[11px] text-zinc-400 font-normal">{fr ? "299 CHF/mois · 6 mois · -5% si 1x" : "299 CHF/month · 6 months · -5% if 1x"}</span>
                  </button>
                </div>

                <p className="text-zinc-600 text-[10px]">{fr ? "Paiement sécurisé Stripe ou virement QR · Lien Zoom envoyé après confirmation" : "Stripe or QR-bill payment · Zoom link sent after confirmation"}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-14 lg:py-20 bg-zinc-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-500/15 to-transparent" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="relative">
              <div className="absolute -inset-px bg-gradient-to-br from-brand-500/10 via-transparent to-brand-400/10 rounded-2xl" />
              <div className="relative bg-zinc-900/60 backdrop-blur-sm border border-zinc-800/50 rounded-2xl p-8 sm:p-10">
                <div className="flex flex-col sm:flex-row items-center gap-8">
                  {/* QR Code */}
                  <div className="shrink-0">
                    <div className="relative group">
                      <div className="absolute -inset-3 bg-gradient-to-br from-brand-500/20 to-brand-400/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative bg-white p-3 rounded-xl shadow-2xl shadow-brand-500/5" data-testid="qr-code-coaching">
                        <QRCodeSVG
                          value={`${window.location.origin}/coaching-online`}
                          size={140}
                          bgColor="#ffffff"
                          fgColor="#0a0a0a"
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Text */}
                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">
                      {fr ? "Partagez cette page" : "Share this page"}
                    </h3>
                    <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
                      {fr
                        ? "Scannez ce QR code ou partagez le lien pour acceder directement a cette page depuis votre telephone."
                        : "Scan this QR code or share the link to access this page directly from your phone."
                      }
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/coaching-online`);
                          const btn = document.getElementById('copy-link-btn');
                          if (btn) { btn.textContent = fr ? 'Copie !' : 'Copied!'; setTimeout(() => { btn.textContent = fr ? 'Copier le lien' : 'Copy link'; }, 2000); }
                        }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-semibold hover:bg-brand-500/20 transition-all"
                        data-testid="copy-coaching-link-btn"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        <span id="copy-link-btn">{fr ? "Copier le lien" : "Copy link"}</span>
                      </button>
                      <button
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({ title: 'Body-Up Coaching en Ligne', url: `${window.location.origin}/coaching-online` });
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-semibold hover:bg-zinc-700 transition-all"
                        data-testid="share-coaching-btn"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        {fr ? "Partager" : "Share"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Payment Modal pour CTA "Démarrer maintenant" */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ isOpen: false, item: null })}
        item={paymentModal.item}
      />
    </main>
  );
};


// Terms Page

export default CoachingOnlinePage;
